/**
 * Suspend or reactivate a tenant (vendor.admin, phase 7b). Suspending needs a
 * reason — it is what the tenant's users and nodes are told with every
 * refusal — and says plainly what it does: users refused within five
 * minutes, site links refused, productions kept running on the nodes.
 * Reactivating takes an optional note for the vendor's activity log.
 */
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Label } from '../ui/label.js';
import { Textarea } from '../ui/textarea.js';

export type SuspendTenantDialogProps = {
  /** Which action, or null for closed. */
  readonly mode: 'suspend' | 'reactivate' | null;
  readonly tenant: { readonly tenantId: string; readonly name: string } | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  /** The reason (suspend) or the note (reactivate). */
  readonly onConfirm: (text: string) => void;
};

function SuspendForm({ mode, tenant, pending, error, onClose, onConfirm }: SuspendTenantDialogProps & { readonly mode: 'suspend' | 'reactivate' }) {
  const [text, setText] = useState('');
  const suspend = mode === 'suspend';
  const ready = !suspend || text.trim() !== '';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onConfirm(text.trim());
  };
  const name = tenant === null ? 'this tenant' : `${tenant.name} (${tenant.tenantId})`;
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{suspend ? 'Suspend tenant' : 'Reactivate tenant'}</DialogTitle>
        <DialogDescription>
          {suspend
            ? `${name}: every user is refused (within five minutes) and every node’s site link is refused, both with your reason. Nodes keep running their productions locally.`
            : `${name}: its users and nodes are let back in.`}
        </DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="tenant-status-text">{suspend ? 'Reason (shown to the tenant)' : 'Note (optional, for the activity log)'}</Label>
        <Textarea id="tenant-status-text" value={text} onChange={(e) => setText(e.target.value)} disabled={pending} maxLength={500} autoFocus />
        {suspend ? <FieldDescription>Required. The tenant’s users see it on every refused request.</FieldDescription> : null}
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" variant={suspend ? 'destructive' : 'default'} disabled={!ready || pending} data-testid="tenant-status-confirm">
          {pending ? 'Working…' : suspend ? 'Suspend' : 'Reactivate'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function SuspendTenantDialog(props: SuspendTenantDialogProps) {
  const { mode } = props;
  return (
    <Dialog
      open={mode !== null}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogContent data-testid="tenant-status-dialog">{mode === null ? null : <SuspendForm {...props} mode={mode} key={`${mode}:${props.tenant?.tenantId ?? ''}`} />}</DialogContent>
    </Dialog>
  );
}
